/* prettier-ignore */

import { inject, Inject, Injectable, signal } from "@angular/core";
import { Meta, Title } from "@angular/platform-browser";
import { TranslateService } from "@ngx-translate/core";
import { RestService } from "./rest.service";
import { ChatBotScriptService } from "./chatbot.service";
import { BehaviorSubject, Observable } from "rxjs";
import { DOCUMENT } from "@angular/common";
import { GoogleTranslateService } from "./google-translate.service";
import { Router } from "@angular/router";

@Injectable({
  providedIn: "root",
})

export class ToolsService {
    private isAppSubject = new BehaviorSubject<boolean | null>(null);
    private isDistributorSubject = new BehaviorSubject<boolean | null>(null);
    isApp$ = this.isAppSubject.asObservable();
    isDistributor$ = this.isDistributorSubject.asObservable();
    
    private _router = inject(Router);

    homeDistributor: any = null;
    menuIsShown: boolean = false;
    showMenuDropdown: any;

    enabledInstallBanner = signal(true);
    showRouteSpinner = signal(false);
    showHeader = signal(false);
    languages = signal([]);

    isPreProduction = signal(false);
    isCorporativeColorsLoaded = signal(false);
    isAppLoaded = signal(false);
    isTechEditMode = signal(false);
    isTechMode = signal(false);
    isRegisterMode = signal(false);
    isInfoRoutePage = signal(false);

    headerLangType = signal('flags');

  constructor(
    @Inject(DOCUMENT) private document: Document,
    private meta: Meta,
    private titleService: Title,
    public translate: TranslateService,
    private rest: RestService,
    private chatService: ChatBotScriptService,
    public googleTranslateService: GoogleTranslateService,
  ) {}

  enableSpinner(){
    this.showRouteSpinner.update(prev => true)
  }

  disableSpinner(){
    this.showRouteSpinner.update(prev => false)
  }

  enableHeader(){
    this.showHeader.update(prev => true);
  }

  disableHeader(){
    this.showHeader.update(prev => false);
  }

  setIsApp(value: boolean) {
    // this.setAppStarted();
    this.isAppSubject.next(value);
  }

  setIsDistributor(value: boolean) {
    // this.setAppStarted();
    this.isDistributorSubject.next(value);
  }

  setAppStarted(){
    this.setCorporativeColors();
    this.enableHeader();
    this.isAppLoaded.set(true)
  }

  setCorporativeColors(){
    this.setCorporiveColors();
    this.changeThemeColor(this.rest?.device?.style?.colorCorp1 || "#000");
    this.isCorporativeColorsLoaded.update(prev => true);
  }

  get isApp(): boolean | null {
    return this.isAppSubject.value;
  }

  get isDistributor(): boolean | null {
    return this.isDistributorSubject.value;
  }

  getDistributorUrl(name) {
    let url = new URL(window.location.href);
    let parts = url.hostname.split(".");
    parts.shift();
    url.hostname = parts.join(".");
    url.hostname = `${name}.${url.hostname}`;
    url.pathname = url.pathname.substring(0, url.pathname.lastIndexOf("/"));
    url.search = "";
    return url.toString();
  }

  //--------------------------------------------------------------------------------------------------------------------------------------------------
  //------------------------------------------------------------ TEMP LOCAL STORAGE ------------------------------------------------------------------  
  setWithExpiry(key, value, ttl) {
    const now = new Date();
    const item = { value: value, expiry: now.getTime() + ttl };
    localStorage.setItem(key, JSON.stringify(item));
  }
  getWithExpiry(key) {
    const itemStr = localStorage.getItem(key);
    if (!itemStr) 
      return null;
    
    const item = JSON.parse(itemStr);
    const now = new Date();
    if (now.getTime() > item.expiry) {
      localStorage.removeItem(key);
      return null;
    }
    return item.value;
  }

  //--------------------------------------------------------------------------------------------------------------------------------------------------
  //--------------------------------------------------------------- SLUGFUNCTION ---------------------------------------------------------------------
  slugify(str) {
    return str
      .toLowerCase()
      .trim()
      .replace(/[^\w\s-]/g, "")
      .replace(/[\s_-]+/g, "-")
      .replace(/^-+|-+$/g, "");
  }
  //--------------------------------------------------------------------------------------------------------------------------------------------------
  //--------------------------------------------------------------- ERROR IMAGEN ----------------------------------------------------------------------
  onImageError(e: Event, src: string = null) {
    if (!src) src = null;
    
    if(src && src.includes('undefined')) src = null;
    
    const target = e.target as HTMLImageElement;
    target.src = src || "/assets/imgs/imageDefault.png";
  }
  
  onImgOfferError(e) {
    e.target.style.objectFit = "contain";
    e.target.src = "/assets/imgs/offerDefault.webp";
  }
  //--------------------------------------------------------------------------------------------------------------------------------------------------
  //--------------------------------------------------------------- SET SEO PAGE ---------------------------------------------------------------------
  public setMetaDescription(content: string) {
    this.meta.updateTag({ name: "description", content: content });
  }
  public setMetaTitle(title: string) {
    this.titleService.setTitle(title);
  }

  setCorporiveColors() {
    const root = document.querySelector(":root") as HTMLElement;
    root.style.setProperty("--main-color", this.rest?.device?.style?.colorCorp1 || "#0c8080");
    root.style.setProperty("--main-color-alpha", `${this.rest?.device?.style?.colorCorp1}33` || "#0c808033");
    root.style.setProperty("--main-color-strong", this.rest?.device?.style?.colorCorp2 || "#2a4b5a");
    root.style.setProperty("--header-color", this.rest?.device?.style?.headerColor || "#00ABAF");
    root.style.setProperty("--compl-color", this.rest?.device?.style?.corpCompl || "#ff3f00");
    root.style.setProperty("--background-color", this.rest?.device?.style?.backgroundColor || "#FFF");
    
    // New corporative colours
    root.style.setProperty("--primary", this.rest?.device?.style?.colorCorp1 || "#00ABAF");
    root.style.setProperty("--primary-dark", "color-mix(in srgb, var(--primary) 40%, black 60%)");
    root.style.setProperty("--primary-light", "color-mix(in srgb, var(--primary) 50%, white 50%)");
    root.style.setProperty("--secondary", this.rest?.device?.style?.colorCorp2 || "#08515D");
    root.style.setProperty("--secondary-dark", "color-mix(in srgb, var(--secondary) 40%, black 60%)");
    root.style.setProperty("--secondary-light", "color-mix(in srgb, var(--secondary) 50%, white 50%)");
    
    // EMT specific colors
    root.style.setProperty("--transportation-consorcio", "#D10002");
  }

  changeLanguage(lang){
    this.chatService.setLang(lang);
    this.translate.setDefaultLang(lang);
    this.translate.use(lang);
  }

  setLanguages(langs){
    this.languages.set(langs?.map((langCode) =>  ({ code: langCode, img: `/assets/langs/${langCode}.svg` }) ) || [])
  }

  getLocation() {
    return new Observable((observer) => {
      if ("geolocation" in navigator) {
        navigator.geolocation.getCurrentPosition(
          (position) => observer.next(position.coords),
          (error) => observer.error(error),
          { enableHighAccuracy: true }
        );
      } else observer.error("La geolocalización no está disponible en este navegador.");
    });
  }

  getSubdomain(url) {
    let domain = url;
    if (url.includes("://")) domain = url.split("://")[1];
    const subdomain = domain.split(".");
    let urlSegments = 2;
    if (domain.includes("localhost")) 
      urlSegments = 1;
  
    return subdomain.length > urlSegments ? subdomain[0] : null;
  }

  changeThemeColor(color: string) {
    this.document.querySelector('meta[name="theme-color"]').setAttribute("content", color);
  }

  toggleMenu(event: any) {
    event.stopPropagation();
    if (event.type === "ionScrollStart" && !this.menuIsShown) return;
    this.menuIsShown = !this.menuIsShown;
  }

  getCurrentLangDates(){
    const currentLang = this.translate.currentLang;
    switch (currentLang){
      case 'va': return 'ca';
      default: return currentLang;
    }
  }

  public getBrowserLang(): string {
    //Español por defecto en caso de no detectar idioma
    let lang = 'es';

    if (navigator.languages && navigator.languages.length) 
      lang = navigator.languages[0];
    else
      lang = navigator.language;

    if (lang === 'zh-CN' || lang === 'zh-TW')
      return lang;

    if (lang.includes('-'))
      return lang.split('-')?.[0] ?? 'es';

    return lang ?? 'es';
  }

  
  //--------------------------------------------------------------------------------------------------------------------------------------------------
  //--------------------------------------------------------------- Check route ---------------------------------------------------------------------
  isRoutePage(route: string): boolean {
    const currentUrl = this._router.url;
    return currentUrl.includes(route);
  }
}